import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiError } from "@/lib/apiClient";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface DashboardState {
  loading: boolean;
  error: ApiError | null;
  stats: any;
  chartData: any[];
  analytics: any;
  transactions: any[];
  topProducts: any[];
  activeOrders: any[];
  customers: any[];
  campaignData: any;
  salesByState: any;
  salesByCountries: any;
  stockAnalysis: any;
  trafficSources: any;
  snapshot: any;
  panelLoading: {
    [key: string]: boolean;
  };
}

const initialState: DashboardState = {
  loading: false,
  error: null,
  stats: null,
  chartData: [],
  analytics: null,
  transactions: [],
  topProducts: [],
  activeOrders: [],
  customers: [],
  campaignData: null,
  salesByState: null,
  salesByCountries: null,
  stockAnalysis: null,
  trafficSources: null,
  snapshot: null,
  panelLoading: {},
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    fetchDashboardRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDashboardSuccess: (
      state,
      action: PayloadAction<{
        stats?: any;
        chartData?: any[];
        analytics?: any;
        transactions?: any[];
        topProducts?: any[];
        activeOrders?: any[];
        customers?: any[];
        campaignData?: any;
        salesByState?: any;
        salesByCountries?: any;
        stockAnalysis?: any;
        trafficSources?: any;
        snapshot?: any;
      }>,
    ) => {
      state.loading = false;
      state.error = null;
      if (action.payload.stats !== undefined)
        state.stats = action.payload.stats;
      if (action.payload.chartData !== undefined)
        state.chartData = action.payload.chartData;
      if (action.payload.analytics !== undefined)
        state.analytics = action.payload.analytics;
      if (action.payload.transactions !== undefined)
        state.transactions = action.payload.transactions;
      if (action.payload.topProducts !== undefined)
        state.topProducts = action.payload.topProducts;
      if (action.payload.activeOrders !== undefined)
        state.activeOrders = action.payload.activeOrders;
      if (action.payload.customers !== undefined)
        state.customers = action.payload.customers;
      if (action.payload.campaignData !== undefined)
        state.campaignData = action.payload.campaignData;
      if (action.payload.salesByState !== undefined)
        state.salesByState = action.payload.salesByState;
      if (action.payload.salesByCountries !== undefined)
        state.salesByCountries = action.payload.salesByCountries;
      if (action.payload.stockAnalysis !== undefined)
        state.stockAnalysis = action.payload.stockAnalysis;
      if (action.payload.trafficSources !== undefined)
        state.trafficSources = action.payload.trafficSources;
      if (action.payload.snapshot !== undefined)
        state.snapshot = action.payload.snapshot;
    },
    fetchDashboardFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    refreshPanelRequest: (state, action: PayloadAction<{ panel: string }>) => {
      if (!state.panelLoading) {
        state.panelLoading = {};
      }
      state.panelLoading[action.payload.panel] = true;
    },
    refreshPanelSuccess: (
      state,
      action: PayloadAction<{ panel: string; data: any }>,
    ) => {
      if (!state.panelLoading) {
        state.panelLoading = {};
      }
      state.panelLoading[action.payload.panel] = false;
      const { panel, data } = action.payload;
      switch (panel) {
        case "statistics":
          state.stats = data;
          break;
        case "earnings":
          state.chartData = data.chartData || data;
          break;
        case "analytics":
          state.analytics = data;
          break;
        case "transactions":
          state.transactions = data;
          break;
        case "topProducts":
          state.topProducts = data;
          break;
        case "activeOrders":
          state.activeOrders = data;
          break;
        case "customers":
          state.customers = data;
          break;
        case "campaignData":
          state.campaignData = data;
          break;
        case "salesByState":
          state.salesByState = data;
          break;
        case "salesByCountries":
          state.salesByCountries = data;
          break;
        case "stockAnalysis":
          state.stockAnalysis = data;
          break;
        case "trafficSources":
          state.trafficSources = data;
          break;
      }
    },
    refreshPanelFailure: (
      state,
      action: PayloadAction<{ panel: string; error: ApiError }>,
    ) => {
      if (!state.panelLoading) {
        state.panelLoading = {};
      }
      state.panelLoading[action.payload.panel] = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => {
      console.log(
        `[dashboard.slice] 🔄 Received resetStoreSpecificState, resetting to initialState`,
      );
      return initialState;
    });
  },
});

export const {
  fetchDashboardRequest,
  fetchDashboardSuccess,
  fetchDashboardFailure,
  refreshPanelRequest,
  refreshPanelSuccess,
  refreshPanelFailure,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
